import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Upload, FileSpreadsheet } from "lucide-react";

function parseCSV(text) {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (lines.length < 2) return { rows: [], errors: ["Need at least a header row and one data row."] };
  const headers = lines[0].split(",").map((h) => h.trim());
  const rows = [];
  const errors = [];
  for (let i = 1; i < lines.length; i++) {
    // Naive CSV split — supports quoted commas
    const cells = [];
    let cur = "";
    let inQ = false;
    for (const ch of lines[i]) {
      if (ch === '"') inQ = !inQ;
      else if (ch === "," && !inQ) { cells.push(cur); cur = ""; }
      else cur += ch;
    }
    cells.push(cur);
    if (cells.length !== headers.length) {
      errors.push(`Row ${i + 1}: column count mismatch (${cells.length} vs ${headers.length}).`);
      continue;
    }
    const row = {};
    headers.forEach((h, idx) => {
      const v = cells[idx].trim();
      row[h] = v === "" ? undefined : v;
    });
    rows.push(row);
  }
  return { rows, errors };
}

export default function BulkImportDialog({
  open, onOpenChange, endpoint, entityLabel, sampleHeaders, onDone,
}) {
  const [text, setText] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const doPreview = () => {
    const { rows, errors } = parseCSV(text);
    setPreview({ rows, errors });
  };

  const doImport = async () => {
    const { rows, errors } = preview || parseCSV(text);
    if (rows.length === 0) return toast.error("No valid rows to import");
    setBusy(true);
    try {
      const { data } = await api.post(endpoint, { rows });
      setResult(data);
      toast.success(`Imported ${data.created} ${entityLabel}`);
      if (onDone) onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setText("");
    setPreview(null);
    setResult(null);
  };

  const useSample = () => {
    setText(sampleHeaders.header + "\n" + sampleHeaders.samples.join("\n"));
    setPreview(null);
    setResult(null);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-[var(--yc-emerald)]" /> Bulk import {entityLabel}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="text-xs text-slate-600 leading-relaxed">
            Paste CSV rows below. First row must be the header. Or click <button className="text-[var(--yc-emerald)] underline" onClick={useSample} data-testid="bulk-load-sample">Load sample</button> to see the format.
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-md p-2 font-mono text-[11px] overflow-x-auto whitespace-pre">
            {sampleHeaders.header}
          </div>
          <Textarea
            rows={10}
            value={text}
            onChange={(e) => { setText(e.target.value); setPreview(null); setResult(null); }}
            placeholder="Paste CSV rows here…"
            className="font-mono text-xs"
            data-testid="bulk-csv-input"
          />

          <div className="flex gap-2">
            <Button variant="outline" onClick={doPreview} disabled={!text.trim()} data-testid="bulk-preview-button">Preview</Button>
            <Button onClick={doImport} disabled={busy || !text.trim()} className="bg-[var(--yc-navy)] hover:bg-[var(--yc-navy-soft)]" data-testid="bulk-import-button">
              {busy ? "Importing…" : `Import ${preview?.rows?.length || ""} rows`}
            </Button>
          </div>

          {preview && (
            <div className="rounded-md border border-slate-200 p-3 text-xs">
              <div className="font-medium text-[var(--yc-navy)]">
                {preview.rows.length} parseable rows · {preview.errors.length} parse errors
              </div>
              {preview.errors.length > 0 && (
                <ul className="mt-1.5 text-[var(--yc-rose)] list-disc pl-5">{preview.errors.slice(0, 5).map((e, i) => <li key={i}>{e}</li>)}</ul>
              )}
              {preview.rows.length > 0 && (
                <div className="mt-2 font-mono">First row: {JSON.stringify(preview.rows[0])}</div>
              )}
            </div>
          )}

          {result && (
            <div className="rounded-md border border-emerald-200 bg-emerald-50/50 p-3 text-xs" data-testid="bulk-result">
              Imported <b>{result.created}</b> of <b>{result.total_rows}</b> rows.
              {result.errors?.length > 0 && (
                <ul className="mt-1.5 text-[var(--yc-rose)] list-disc pl-5">
                  {result.errors.slice(0, 5).map((e, i) => <li key={i}>Row {e.row}: {e.error}</li>)}
                </ul>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export const CANDIDATE_TEMPLATE = {
  header: "name,role_applied,department,branch,contact,source,current_location,expected_salary,notice_period,work_experience",
  samples: [
    "Anisha Kapoor,Aesthetic Doctor,Clinical,Sarita Vihar,+91-9111111111,Referral,Delhi,60000,30 days,3 years",
    "Rohit Bhandari,Tele-caller,Telesales,Mathura,+91-9222222222,Naukri,Mathura,25000,15 days,1 year",
  ],
};

export const EMPLOYEE_TEMPLATE = {
  header: "name,designation,department,branch,joining_date,phone,emergency_contact_name,emergency_contact_phone,emergency_contact_relation",
  samples: [
    "Radhika Menon,Aesthetic Doctor,Clinical,Sarita Vihar,2025-06-01,+91-9333333333,Suresh Menon,+91-9444444444,Spouse",
    "Arjun Bhatia,Tele-caller,Telesales,Jaipur,2025-11-15,+91-9555555555,Rita Bhatia,+91-9666666666,Parent",
  ],
};
